import { LitElement, html, css } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { consume } from "@lit/context";
import {
  Chart,
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ChartDataset,
} from "chart.js";
import type { RatingConfig } from "../models";
import { withOpacity } from "../utils/color";
import { themeContext, configContext } from "../contexts";
import { DATASET_COLORS } from "../constants";
import C2S from "canvas2svg";

Chart.register(
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
);

const POINT_STYLES = [
  "circle",
  "triangle",
  "rect",
  "star",
  "cross",
  "rectRot",
  "crossRot",
];

/**
 * Renders a Chart.js radar (spider) chart from a `RatingConfig` and `AppState`.
 * The chart is initialised on first render and updated reactively whenever
 * `config`, `state`, or `theme` changes. Updates are throttled to ensure
 * smooth performance. The Chart instance is destroyed on disconnect.
 */
@customElement("bab-spider-chart")
export class BabSpiderChart extends LitElement {
  static styles = css`
    :host {
      display: block;
      width: 100%;
      position: relative;
    }
    canvas {
      width: 100% !important;
      height: auto !important;
      display: block;
    }
    .watermark {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--bab-text-secondary);
      opacity: 0.5;
      pointer-events: none;
      text-align: center;
      width: 100%;
      padding: 0 40px;
      box-sizing: border-box;
    }
    .chart-title {
      text-align: center;
      margin-bottom: 12px;
      font-weight: 700;
      font-size: 1.1rem;
      color: var(--bab-text);
      min-height: 1.5rem;
    }
  `;

  @consume({ context: configContext, subscribe: true })
  @state()
  config!: RatingConfig;

  /** List of rating sets to display. */
  @property({ type: Array })
  datasets: import("../models").RatingSet[] = [];

  @consume({ context: themeContext, subscribe: true })
  @state()
  theme: import("../models").Theme = "system";

  @query("canvas")
  private _canvas!: HTMLCanvasElement;

  @state() private _isEmpty = true;

  private _chart: Chart | null = null;
  private _updateRequested = false;

  private _getChartTitle(): string {
    const namedDatasets = this.datasets
      .map(d => d.name?.trim())
      .filter(Boolean);
    
    if (namedDatasets.length === 0) return "";
    return namedDatasets.join(" vs ");
  }

  private _labels(): string[] {
    return this.config.attributes.map((a) => a.name);
  }

  private _datasets(): ChartDataset<"radar">[] {
    return this.datasets.map((set, i) => {
      const color = set.color || DATASET_COLORS[i % DATASET_COLORS.length];
      const pointStyle = POINT_STYLES[i % POINT_STYLES.length];
      return {
        label: set.name || `${i + 1}`,
        data: this.config.attributes.map((a) => set.ratings[a.id] ?? null),
        backgroundColor: withOpacity(color, 0.2),
        borderColor: color,
        borderWidth: 2,
        pointBackgroundColor: color,
        pointStyle,
        pointRadius: 5,
        pointHoverRadius: 7,
      } as ChartDataset<"radar">;
    });
  }

  private _getThemeValue(name: string): string {
    return getComputedStyle(this).getPropertyValue(name).trim();
  }

  private _isDarkMode(): boolean {
    if (this.theme === "dark") return true;
    if (this.theme === "light") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  }

  private _initChart() {
    if (this._chart) return;

    const isDark = this._isDarkMode();
    const gridColor = isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.1)";
    const textColor = this._getThemeValue("--bab-text") || (isDark ? "#ffffff" : "#000000");

    this._chart = new Chart(this._canvas, {
      type: "radar",
      data: {
        labels: this._labels(),
        datasets: this._datasets(),
      },
      options: {
        responsive: true,
        animation: { duration: 500 },
        layout: { padding: 20 },
        plugins: {
          legend: { 
            display: this.datasets.length > 1,
            position: "bottom",
            labels: { 
              color: textColor,
              usePointStyle: true,
              padding: 20
            }
          },
          tooltip: {
            callbacks: {
              title: (items) => {
                if (!items.length) return "";
                const idx = items[0].dataIndex;
                return (items[0].chart.data.labels?.[idx] as string) ?? "";
              },
              label: (ctx) => {
                const val = ctx.raw as number | null;
                const setLabel = ctx.dataset.label || "";
                if (val === null) return `${setLabel}: Not rated`;
                const ratingLabel = this.config.defaultLabels?.[val] ?? String(val);
                return `${setLabel}: ${val} — ${ratingLabel}`;
              },
            },
          },
        },
        scales: {
          r: {
            min: this.config.versions[this.config.currentVersion]?.minRating ?? 0,
            max:
              this.config.versions[this.config.currentVersion]?.maxRating ?? 5,
            ticks: { 
              stepSize: this.config.versions[this.config.currentVersion]?.labelInterval ?? 1,
              display: false 
            },
            grid: { color: gridColor },
            angleLines: { color: gridColor },
            pointLabels: { 
              font: { size: 12, weight: "bold" },
              color: textColor 
            },
          },
        },
      },
    });
  }

  private _requestUpdate() {
    if (this._updateRequested) return;
    this._updateRequested = true;
    requestAnimationFrame(() => {
      this._updateChart();
      this._updateRequested = false;
    });
  }

  private _updateChart() {
    if (!this._chart) return;

    const isDark = this._isDarkMode();
    const gridColor = isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.1)";
    const textColor = this._getThemeValue("--bab-text") || (isDark ? "#ffffff" : "#000000");

    this._chart.data.labels = this._labels();
    this._chart.data.datasets = this._datasets();
    this._isEmpty = !this.datasets.some(set => 
      Object.values(set.ratings).some(v => v !== null)
    );

    const rScale = this._chart.options.scales?.r as any;
    if (rScale) {
      rScale.min = this.config.versions[this.config.currentVersion]?.minRating ?? 0;
      rScale.max = this.config.versions[this.config.currentVersion]?.maxRating ?? 5;
      rScale.ticks.stepSize = this.config.versions[this.config.currentVersion]?.labelInterval ?? 1;
      rScale.grid.color = gridColor;
      rScale.angleLines.color = gridColor;
      rScale.pointLabels.color = textColor;
    }

    if (this._chart.options.plugins?.legend?.labels) {
      this._chart.options.plugins.legend.display = this.datasets.length > 1;
      this._chart.options.plugins.legend.labels.color = textColor;
      this._chart.options.plugins.legend.labels.usePointStyle = true;
    }

    this._chart.update();
  }

  firstUpdated() {
    this._initChart();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has("config") || changed.has("datasets") || changed.has("theme")) {
      this._requestUpdate();
    }
  }

  private _query = window.matchMedia("(prefers-color-scheme: dark)");
  private _onThemeChange = () => {
    if (this.theme === "system") this._requestUpdate();
  };

  connectedCallback() {
    super.connectedCallback();
    this._query.addEventListener("change", this._onThemeChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._query.removeEventListener("change", this._onThemeChange);
    this._chart?.destroy();
    this._chart = null;
  }

  /**
   * Returns a Base64 data URL of the current chart, including metadata.
   * @param type - Image format (default: 'image/png').
   * @returns The data URL string.
   */
  public getImageDataUrl(type = "image/png"): string {
    const tempCanvas = document.createElement("canvas");
    const ctx = tempCanvas.getContext("2d")!;
    const padding = 40;
    const headerHeight = 80;
    const footerHeight = 40;

    tempCanvas.width = this._canvas.width;
    tempCanvas.height = this._canvas.height + headerHeight + footerHeight;

    const isDark = this._isDarkMode();
    ctx.fillStyle = isDark ? "#1f2937" : "#ffffff";
    ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

    ctx.drawImage(this._canvas, 0, headerHeight);

    const textColor = isDark ? "#ffffff" : "#000000";
    ctx.fillStyle = textColor;
    ctx.textAlign = "center";
    
    const title = this._getChartTitle();

    let fontSize = 24;
    ctx.font = `bold ${fontSize}px system-ui, -apple-system, sans-serif`;
    while (ctx.measureText(title).width > tempCanvas.width - 2 * padding && fontSize > 12) {
      fontSize -= 2;
      ctx.font = `bold ${fontSize}px system-ui, -apple-system, sans-serif`;
    }
    
    ctx.fillText(title, tempCanvas.width / 2, padding + (24 - fontSize) / 2 + 10);

    ctx.font = "14px system-ui, -apple-system, sans-serif";
    ctx.fillStyle = isDark ? "#9ca3af" : "#6b7280";
    const dateStr = new Date().toLocaleDateString();
    ctx.fillText(`Generated on ${dateStr}`, tempCanvas.width / 2, tempCanvas.height - 20);

    return tempCanvas.toDataURL(type);
  }

  /**
   * Returns a data URL for an SVG version of the chart.
   * @returns The data URL string.
   */
  public getSvgDataUrl(): string {
    const width = this._canvas.width;
    const height = this._canvas.height;
    const svgContext = new C2S(width, height);
    
    // Create a temporary chart using the SVG context
    const tempChart = new Chart(svgContext as unknown as HTMLCanvasElement, {
      type: "radar",
      data: {
        labels: this._labels(),
        datasets: this._datasets(),
      },
      options: {
        ...this._chart?.options,
        animation: false,
        responsive: false,
      } as any,
    });

    const svgString = svgContext.getSerializedSvg();
    tempChart.destroy();
    
    return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgString);
  }

  render() {
    return html`
      <div class="chart-title">${this._getChartTitle()}</div>
      <canvas></canvas>
      ${this._isEmpty ? html`<div class="watermark">Rate some ${this.config.attributeTermPlural || "skills"} to see your chart</div>` : ""}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bab-spider-chart": BabSpiderChart;
  }
}
